import * as React from "react";

import {
  Alert,
  Button,
  FormField,
  Input,
  Select,
  type SelectOption,
  Spinner,
  Textarea,
} from "Components/ui";
import { ManualPaymentMethod } from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import {
  formatDate,
  formatINR,
  isDateOnly,
  paiseToRupeesInput,
  rupeesToPaise,
  todayIST,
} from "utils/format";
import DialogShell from "Routes/Billing/Components/DialogShell";
import { PAYMENT_METHOD_LABELS } from "Routes/Billing/Components/StatusChips";
import { DateInput } from "Routes/Billing/Components/ui";
import { closeAfterFlush } from "Routes/Billing/hooks";
import { useInvoiceList, usePaymentActions } from "../data";

const METHODS: ManualPaymentMethod[] = ["bank_transfer", "upi", "cheque", "cash", "other"];
const METHOD_OPTIONS: SelectOption<ManualPaymentMethod>[] = METHODS.map((value) => ({
  value,
  label: PAYMENT_METHOD_LABELS[value],
}));
const MAX_AMOUNT_PAISE = 100000000000;

interface RecordPaymentModalProps {
  tenantId: string;
  defaultInvoiceId: string | null;
  onClose: () => void;
}

/** Manual payment against an open invoice (billing/approve, reason required). */
const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  tenantId,
  defaultInvoiceId,
  onClose,
}) => {
  const today = todayIST();
  const openInvoices = useInvoiceList({
    tenantId,
    status: ["issued", "partially_paid"],
    pageSize: 100,
  });
  const actions = usePaymentActions();

  const [invoiceId, setInvoiceId] = React.useState<string>("");
  const [amount, setAmount] = React.useState<string>("");
  const [method, setMethod] = React.useState<ManualPaymentMethod>("bank_transfer");
  const [paidAt, setPaidAt] = React.useState<string>(today);
  const [reference, setReference] = React.useState<string>("");
  const [reason, setReason] = React.useState<string>("");
  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const invoices = React.useMemo(
    () => (openInvoices.data ? openInvoices.data.data : []),
    [openInvoices.data]
  );
  const selected = invoices.find((invoice) => invoice._id === invoiceId) || null;

  const selectInvoice = React.useCallback(
    (id: string) => {
      setInvoiceId(id);
      const match = invoices.find((invoice) => invoice._id === id);
      setAmount(match ? paiseToRupeesInput(Math.max(match.balance, 0)) : "");
    },
    [invoices]
  );

  // Preselect the requested invoice (or the earliest due one) once the list arrives.
  React.useEffect(() => {
    if (invoiceId || invoices.length === 0) {
      return;
    }
    // A requested invoice that is no longer open (paid meanwhile) is not swapped for another one:
    // the admin picks explicitly rather than booking money against the wrong invoice.
    if (defaultInvoiceId) {
      const requested = invoices.find((invoice) => invoice._id === defaultInvoiceId);
      if (requested) {
        selectInvoice(requested._id);
      }
      return;
    }
    selectInvoice(invoices.slice().sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))[0]._id);
  }, [defaultInvoiceId, invoiceId, invoices, selectInvoice]);

  const amountPaise = rupeesToPaise(amount);
  const errors = {
    invoice: selected ? null : "Choose the invoice this payment is for.",
    amount:
      amountPaise === null || amountPaise <= 0
        ? "Enter the amount received."
        : amountPaise > MAX_AMOUNT_PAISE
        ? "That amount is too large."
        : null,
    paidAt: !isDateOnly(paidAt)
      ? "Pick the date the money arrived."
      : paidAt > today
      ? "The payment date can't be in the future."
      : null,
    reference: reference.length > 120 ? "Keep the reference under 120 characters." : null,
    reason: !reason.trim()
      ? "A reason is required."
      : reason.trim().length > 500
      ? "Keep it under 500 characters."
      : null,
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const overpaying =
    selected !== null && amountPaise !== null && amountPaise > Math.max(selected.balance, 0);

  const handleSave = async () => {
    setShowErrors(true);
    setSubmitError(null);
    if (hasErrors || !selected || amountPaise === null) {
      return;
    }
    setSaving(true);
    try {
      await actions.record.mutateAsync({
        invoiceId: selected._id,
        input: {
          amount: amountPaise,
          method,
          paidAt,
          reference: reference.trim() || undefined,
          reason: reason.trim(),
        },
      });
      closeAfterFlush(onClose);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
      setSaving(false);
    }
  };

  const fieldError = (key: keyof typeof errors) => (showErrors && errors[key]) || undefined;
  const invoiceOptions: SelectOption[] = invoices.map((invoice) => ({
    value: invoice._id,
    label: (
      <>
        <span className={invoice.number ? "font-mono" : undefined}>
          {invoice.number || "Unnumbered"}
        </span>{" "}
        · due {formatDate(invoice.dueDate)} · {formatINR(invoice.balance)} open
      </>
    ),
  }));

  return (
    <DialogShell
      title="Record payment"
      subtitle="For money received outside the online pay link (bank transfer, UPI, cheque, cash)."
      onClose={saving ? () => undefined : onClose}
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={invoices.length === 0}>
            Record payment
          </Button>
        </>
      }
    >
      {openInvoices.isLoading ? (
        <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
          <Spinner className="h-5 w-5" />
          Loading open invoices…
        </div>
      ) : openInvoices.isError ? (
        <Alert tone="danger" title="Open invoices didn't load">
          {getApiErrorMessage(openInvoices.error)}
        </Alert>
      ) : invoices.length === 0 ? (
        <Alert tone="info" title="No open invoices">
          Payments are recorded against an issued invoice. Issue an invoice first (Charges or
          Invoices tab).
        </Alert>
      ) : (
        <div className="space-y-4">
          <FormField label="Invoice" id="payment-invoice" required error={fieldError("invoice")}>
            <Select
              options={invoiceOptions}
              value={invoiceId}
              onValueChange={selectInvoice}
              placeholder="Choose an invoice…"
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Amount received (₹)"
              id="payment-amount"
              required
              error={fieldError("amount")}
              hint={selected ? `Balance ${formatINR(selected.balance)}` : undefined}
            >
              <Input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="tabular-nums"
              />
            </FormField>
            <FormField label="Method" id="payment-method" required>
              <Select options={METHOD_OPTIONS} value={method} onValueChange={setMethod} />
            </FormField>
            <FormField label="Received on" id="payment-date" required error={fieldError("paidAt")}>
              <DateInput
                id="payment-date"
                value={paidAt}
                max={today}
                onChange={setPaidAt}
                hasError={Boolean(fieldError("paidAt"))}
              />
            </FormField>
            <FormField
              label="Reference"
              id="payment-reference"
              error={fieldError("reference")}
              hint="UTR, cheque number or receipt id."
            >
              <Input
                type="text"
                value={reference}
                maxLength={120}
                onChange={(event) => setReference(event.target.value)}
                className="font-mono"
              />
            </FormField>
          </div>

          {overpaying && selected && (
            <Alert tone="warning" title="More than the balance">
              The invoice has {formatINR(selected.balance)} open. The extra is kept but the invoice
              is flagged for review.
            </Alert>
          )}

          <FormField
            label="Reason"
            id="payment-reason"
            required
            error={fieldError("reason")}
            hint="Required for approval actions; saved in the audit log."
          >
            <Textarea
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. NEFT received, confirmed with accounts"
            />
          </FormField>

          {submitError && (
            <Alert tone="danger" title="Not recorded">
              {submitError}
            </Alert>
          )}
        </div>
      )}
    </DialogShell>
  );
};

export default RecordPaymentModal;
